import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import { Divider, FormControlLabel, InputAdornment, Stack, Switch } from '@mui/material'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'

import Grid2 from '@mui/material/Grid'
import { SignInScope } from '../Main.scopes'
import { BaseSigninView } from './components/BaseSigninPage'
import { ExternalLoginButton } from './components/ExternalLoginButton'

const LOG = Logger.get('MainView/SingInView')

export const useStyles = makeStyles()(() => ({
    welcomeTitle: {
        fontSize: '1.9rem',
        fontWeight: 700
    },
    welcomeSubtitle: {
        fontSize: '0.95rem',
        color: '#666'
    },
    formCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        boxShadow: 'none',
        background: 'transparent'
    },
    fieldGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },

    fieldLabel: {
        fontSize: '0.9rem',
        fontWeight: 500,
        color: '#000'
    },

    textField: {
        width: '100%',
        '& .MuiFilledInput-root': {
            backgroundColor: '#F0F3F7 !important',
            borderRadius: 8,
            border: '1px solid #E0E3E8',
            padding: '0 16px',
            height: 50,

            '&:hover': {
                backgroundColor: '#E8ECF1 !important'
            },

            '&::before, &::after': { display: 'none' }
        },

        '& .MuiInputBase-input': {
            padding: 0
        },

        '& .Mui-focused .MuiFilledInput-root': {
            border: `1px solid #00E300`
        }
    },
    mainButton: {
        backgroundColor: '#000 !important',
        color: '#fff',
        fontSize: '1rem',
        height: 50,
        borderRadius: 8,
        fontWeight: 600,
        '&:hover': {
            backgroundColor: '#111'
        }
    },
    rememberMe: {
        marginLeft: 'initial'
    },
    forgotPasswordLink: {
        marginRight: '5px',
        alignSelf: 'center',
        color: '#000',
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.85rem',
        cursor: 'pointer',
        textDecoration: 'none',

        '&:hover': {
            textDecoration: 'underline'
        }
    },
    bottomLinks: {
        marginTop: 16,
        fontSize: '0.85rem',
        textAlign: 'center',
        opacity: 0.8,
        lineHeight: 1.5
    }
}))

export function SingInView({ className, scope }: { className?: string; scope: SignInScope }) {
    bindUpdate(React, scope)
    const { classes } = useStyles()

    const handleUserNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => (scope.userName = e.target.value)
    const handlePasswordChanged = (e: React.ChangeEvent<HTMLInputElement>) => (scope.password = e.target.value)
    const handleRememberMeChanged = (e: React.ChangeEvent<HTMLInputElement>) =>
        scope.onChangeRememberMe(e.target.checked).catch(LOG.caught)

    const handleOnPasswordKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') scope.onSignIn().catch(LOG.caught)
    }

    return (
        <BaseSigninView className={className}>
            <Typography className={classes.welcomeTitle}>Bem-vindo de volta</Typography>
            <Typography className={classes.welcomeSubtitle}>Insira suas credenciais para acessar o portal.</Typography>

            <Card className={classes.formCard}>
                {scope.errorMessage && <Alert severity="error">{scope.errorMessage}</Alert>}

                <div className={classes.fieldGroup}>
                    <Typography className={classes.fieldLabel}>Email</Typography>
                    <TextField
                        variant="filled"
                        placeholder="voce@empresa.com.br"
                        className={classes.textField}
                        error={!!scope.userNameError}
                        helperText={scope.userNameError}
                        onChange={handleUserNameChanged}
                        slotProps={{
                            input: {
                                disableUnderline: true,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                                    </InputAdornment>
                                )
                            }
                        }}
                    />
                </div>

                <div className={classes.fieldGroup}>
                    <Typography className={classes.fieldLabel}>Senha</Typography>
                    <TextField
                        type="password"
                        variant="filled"
                        placeholder="******"
                        className={classes.textField}
                        error={!!scope.passwordError}
                        helperText={scope.passwordError}
                        onChange={handlePasswordChanged}
                        onKeyDown={handleOnPasswordKeyDown}
                        slotProps={{
                            input: {
                                disableUnderline: true,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                                    </InputAdornment>
                                )
                            }
                        }}
                    />
                    <Stack
                        direction="row"
                        sx={{
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <FormControlLabel
                            className={classes.rememberMe}
                            control={<Switch checked={scope.rememberMe || false} onChange={handleRememberMeChanged} />}
                            label="Manter conectado"
                        />

                        <Typography
                            onClick={scope.onForgotPassword}
                            className={classes.forgotPasswordLink}
                            component="span"
                        >
                            Esqueceu a senha?
                        </Typography>
                    </Stack>
                </div>

                <Button fullWidth onClick={scope.onSignIn} className={classes.mainButton}>
                    ACESSAR PLATAFORMA
                </Button>
            </Card>

            {(scope.externalAuthConfig?.googleAuth || scope.externalAuthConfig?.microsoftAuth) && (
                <Divider sx={{ my: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#999'
                        }}
                    >
                        OU
                    </Typography>
                </Divider>
            )}

            <Grid2 container spacing={2}>
                {scope.externalAuthConfig?.microsoftAuth && (
                    <Grid2 size={12}>
                        <ExternalLoginButton provider="microsoft" config={scope.externalAuthConfig.microsoftAuth} />
                    </Grid2>
                )}

                {scope.externalAuthConfig?.googleAuth && (
                    <Grid2 size={12}>
                        <ExternalLoginButton provider="google" config={scope.externalAuthConfig.googleAuth} />
                    </Grid2>
                )}
            </Grid2>

            <Typography className={classes.bottomLinks}>
                Ao acessar, você concorda com nossos <strong>Termos de Serviço</strong> e{' '}
                <strong>Política de Privacidade</strong>.
            </Typography>
        </BaseSigninView>
    )
}
